// ────────────────────────────────────────────────────────────────────────────
// Dex query refresh helpers.
//
// Salvaged from the original `realDexImport.ts` during the PR-D1 deletion
// of the multi-mode scan import pipeline (box/detail/journal/catch +
// video import). These three exports survive because they are consumed
// outside the import flow:
//
//   - DEX_TOTALS_EVENT     → Profile.tsx, Friends.tsx, DexView.tsx
//   - invalidateDexQueries → LiveScanModal.tsx, DexView.tsx (and previously
//                             Catch.tsx, which is being deleted in PR-D2)
//   - refetchDexQueriesNow → LiveScanModal.tsx
//
// Behaviour is unchanged from realDexImport.ts. The custom DOM event +
// `fetchQuery` (rather than `refetchQueries`) handling is load-bearing for
// the build-8 "Profile rings stuck at 0 after confirmed scan" telemetry
// case — keep this intact when refactoring further.
// ────────────────────────────────────────────────────────────────────────────
import { queryClient } from "./queryClient";

const DEX_QUERY_KEYS: ReadonlyArray<readonly unknown[]> = [
  ["/api/dex/batches"],
  ["/api/dex/records"],
  ["/api/dex/completion"],
  ["/api/dex/timeline"],
  ["/api/dex/review-queue"],
];

/**
 * v1.2.1(9): Custom DOM event broadcast after every Dex invalidation.
 * Any component that wants to react to Dex totals being updated WITHOUT
 * subscribing to React Query directly (e.g. components that derive a UI
 * cue from "we just got new totals") can listen for this. The
 * scan callers already invalidate the React Query cache; this event is a
 * complementary push channel for ad-hoc consumers. It is also the
 * load-bearing signal for the build-8 "Profile MAIN/SHINY/LUCKY/PERFECT
 * remained 0 after a confirmed scan" complaint — even when the cached
 * query is stale, the explicit DOM event lets the page force a re-render
 * in the next tick.
 */
export const DEX_TOTALS_EVENT = "nexus-dex:totals-updated";

function emitDexTotalsEvent() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(DEX_TOTALS_EVENT));
  } catch {
    /* DOM CustomEvent ctor missing — safe to swallow on SSR. */
  }
}

export function invalidateDexQueries() {
  // Every surface that depends on /api/dex/* gets refreshed in one shot so
  // the Profile ring, Dex KPI strip, and Pokedex completion page all
  // reflect the new rows without requiring a hard reload. We use
  // `refetchType: "all"` so an inactive consumer (e.g. user is on the
  // Companion / Profile tab during the scan and only opens /dex
  // afterwards) ALSO refetches the next time its query mounts. With the
  // global `staleTime: Infinity` this is the only way to guarantee the
  // freshly ingested rows are visible without a force-quit.
  for (const key of DEX_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey: key, refetchType: "all" });
  }
  emitDexTotalsEvent();
}

/**
 * Force an immediate refetch of every Dex-facing query AND wait for them
 * to settle. The LiveScanModal calls this on its final flush so the
 * "Done" tap lands on screens that already reflect the new rows. The
 * promise resolves to a summary the caller can capture into the debug
 * telemetry ring buffer — we expose accepted counts + record totals so
 * Sam can see, in one paste, whether the worker said "I wrote N rows"
 * AND whether the Dex query saw those N rows on the very next read.
 *
 * NOTE: previously this used `refetchQueries`, but that only refetches
 * queries that already exist in the cache. If neither the Dex tab nor
 * the Pokedex page has been mounted in this app launch (very common on
 * a first-run scan started from Companion / Profile), the query is not
 * in the cache at all and `getQueryData` returns undefined — which is
 * how the telemetry ended up showing `recordsCaught:null` even though
 * the worker had the rows. We use `fetchQuery` instead so the query is
 * created on demand, the response is awaited, and we return numeric
 * counters as long as the network call succeeds.
 */
export async function refetchDexQueriesNow(): Promise<{
  batches: {
    total: number | null;
    lastUploadAt: string | null;
    totalRecords: number | null;
    ok: boolean;
    error?: string;
  };
  completion: {
    ultimateDexCaught: number | null;
    recordsCaught: number | null;
    pokedexProgressCaught: number | null;
    ultimateDexTotal: number | null;
    percentComplete: number | null;
    ok: boolean;
    error?: string;
  };
}> {
  const fetchTasks = DEX_QUERY_KEYS.map((key) =>
    queryClient
      .fetchQuery({ queryKey: key, staleTime: 0 })
      .catch((err) => {
        // fetchQuery throws on network failure; we want the caller to
        // see a structured response anyway so we can capture which of
        // the two refetches actually failed.
        return { __error: String((err as Error)?.message ?? err) };
      }),
  );
  const results = await Promise.all(fetchTasks);
  const findResult = (key: readonly unknown[]): any => {
    const idx = DEX_QUERY_KEYS.findIndex((k) => k[0] === key[0]);
    return idx >= 0 ? results[idx] : undefined;
  };
  const batches = findResult(["/api/dex/batches"]);
  const completion = findResult(["/api/dex/completion"]);
  const batchesOk = !!batches && !batches.__error;
  const completionOk = !!completion && !completion.__error;
  if (batchesOk || completionOk) emitDexTotalsEvent();
  return {
    batches: {
      total: batchesOk ? toNum(batches?.stats?.total) : null,
      lastUploadAt: batchesOk ? (batches?.stats?.lastUploadAt ?? null) : null,
      totalRecords: batchesOk ? toNum(batches?.stats?.totalRecords) : null,
      ok: batchesOk,
      error: batchesOk ? undefined : (batches?.__error as string | undefined),
    },
    completion: {
      ultimateDexCaught: completionOk ? toNum(completion?.ultimateDexCaught) : null,
      recordsCaught: completionOk ? toNum(completion?.recordsCaught) : null,
      pokedexProgressCaught: completionOk ? toNum(completion?.pokedexProgressCaught) : null,
      ultimateDexTotal: completionOk ? toNum(completion?.ultimateDexTotal) : null,
      percentComplete: completionOk ? toNum(completion?.percentComplete) : null,
      ok: completionOk,
      error: completionOk ? undefined : (completion?.__error as string | undefined),
    },
  };
}

/** Coerce server numeric fields to a number-or-null. Treats undefined / NaN as null. */
function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
